import { ArrowLeft, BarChart3, Brain } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { DryRunBanner } from "@/components/DryRunBanner";
import { RunDetail } from "@/components/RunDetail";
import { SeverityBadge } from "@/components/SeverityBadge";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useIncident, useIncidentEquipos } from "@/hooks/useIncidents";
import { useSystemHealth } from "@/hooks/useSystemHealth";

export function IncidentDetailPage() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const { data, isPending, error } = useIncident(incidentId);
  const { data: equipos } = useIncidentEquipos(incidentId);
  const healthQuery = useSystemHealth();
  const analysisEnabled = healthQuery.data?.analysis_enabled ?? false;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="outline" size="sm">
          <Link to="/">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        </Button>
        <h1 className="text-xl font-semibold tracking-tight">Detalle de incidente</h1>
        <div className="ml-auto flex items-center gap-2">
          {data?.grafana_url && (
            <Button asChild variant="outline" size="sm">
              <a href={data.grafana_url} target="_blank" rel="noopener noreferrer">
                <BarChart3 className="size-4" /> Ver métricas
              </a>
            </Button>
          )}
          {analysisEnabled && incidentId && (
            <Button asChild size="sm">
              <Link to={`/incidents/${incidentId}/analysis`}>
                <Brain className="size-4" /> Análisis profundo
              </Link>
            </Button>
          )}
        </div>
      </div>

      {isPending && (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          Cargando…
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error instanceof ApiError && error.status === 404
            ? "Incidente no encontrado."
            : error instanceof Error
              ? error.message
              : "Error al cargar el incidente."}
        </div>
      )}

      {data && (
        <>
          {/* 1. Cabecera */}
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center gap-3">
                <SeverityBadge value={data.priority} />
                <StatusBadge value={data.status} />
                <span className="font-mono text-sm text-muted-foreground">
                  {data.incident_id}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 text-sm sm:grid-cols-3">
                <Field label="Creado" value={new Date(data.created_at).toLocaleString()} />
                <Field
                  label="Última actualización"
                  value={new Date(data.updated_at).toLocaleString()}
                />
                <Field
                  label="Resuelto"
                  value={data.resolved_at ? new Date(data.resolved_at).toLocaleString() : "—"}
                />
                {data.resolution_reason && (
                  <Field
                    label="Motivo de resolución"
                    value={data.resolution_reason}
                    span="sm:col-span-3"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* 2. Correlación */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Correlación</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Estrategia" value={data.correlation_strategy} />
                <Field label="Dimensión" value={data.correlation_dimension} />
                <Field
                  label="Llave"
                  value={data.correlation_key}
                  mono
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Hosts afectados
                </div>
                {/* Un incidente junta los equipos que cayeron a la vez. Cuando
                    unos se recuperan y otros no, sigue abierto con razon
                    —cerrarlo taparia al que falta— pero hay que poder ver
                    cual es cual. El del 22-ago traia 19 agencias y solo Leon
                    seguia caida. */}
                {equipos && equipos.zabbix_disponible && data.affected_hosts.length > 1 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {equipos.activos.length === 0
                      ? "Ninguno sigue caído: el problema ya terminó en todos."
                      : `${equipos.activos.length} de ${data.affected_hosts.length} sigue${
                          equipos.activos.length === 1 ? "" : "n"
                        } caído${equipos.activos.length === 1 ? "" : "s"}.`}
                  </p>
                )}
                {equipos && !equipos.zabbix_disponible && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    No se pudo consultar el monitoreo, así que no se sabe cuáles siguen caídos.
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {data.affected_hosts.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    data.affected_hosts.map((h) => {
                      const recuperado =
                        equipos?.zabbix_disponible && equipos.recuperados.includes(h);
                      return (
                        <Badge
                          key={h}
                          variant="outline"
                          className={
                            recuperado
                              ? "font-mono text-xs text-muted-foreground line-through"
                              : "font-mono text-xs"
                          }
                          title={recuperado ? "Ya se recuperó" : undefined}
                        >
                          {h}
                        </Badge>
                      );
                    })
                  )}
                </div>
              </div>
              {data.affected_host_groups.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Grupos
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {data.affected_host_groups.map((g) => (
                      <Badge key={g} variant="outline" className="font-mono text-xs">
                        {g}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Resumen
                </div>
                <p className="mt-1 whitespace-pre-wrap">{data.summary}</p>
              </div>
            </CardContent>
          </Card>

          {/* 3. Disparadores */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Disparadores</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Triggers ({data.trigger_descriptions.length})
                </div>
                <ul className="mt-1 space-y-1">
                  {data.trigger_descriptions.length === 0 ? (
                    <li className="text-muted-foreground">—</li>
                  ) : (
                    data.trigger_descriptions.map((t, idx) => (
                      <li key={`${idx}-${t}`} className="rounded border bg-muted/30 px-2 py-1">
                        {t}
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Métricas
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {data.metric_names.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    data.metric_names.map((m) => (
                      <Badge key={m} variant="secondary" className="font-mono text-xs">
                        {m}
                      </Badge>
                    ))
                  )}
                </div>
              </div>
              {data.raw_severities.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    Severidades originales
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {data.raw_severities.map((s, idx) => (
                      <Badge key={`${idx}-${s}`} variant="outline" className="text-xs">
                        {s}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 4. Contexto de eventos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contexto de eventos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <strong className="tabular-nums">{data.event_count}</strong> eventos
                correlacionados entre{" "}
                <span className="font-mono text-xs">
                  {new Date(data.earliest_event_at).toLocaleString()}
                </span>{" "}
                y{" "}
                <span className="font-mono text-xs">
                  {new Date(data.latest_event_at).toLocaleString()}
                </span>
                .
              </p>
              <p className="text-xs text-muted-foreground">
                Sin timeline granular por evento (DF6-07).
              </p>
            </CardContent>
          </Card>

          {/* 5. Playbook runs */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold tracking-tight">
                Playbook runs ({data.playbook_runs.length})
              </h2>
            </div>
            {data.playbook_runs.length > 0 && <DryRunBanner />}
            {data.playbook_runs.length === 0 ? (
              <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
                Ningún playbook se ejecutó (ni en dry-run) contra este incidente.
              </div>
            ) : (
              <div className="space-y-2">
                {data.playbook_runs.map((run) => (
                  <RunDetail key={run.run_id} run={run} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono = false,
  span,
}: {
  label: string;
  value: string;
  mono?: boolean;
  span?: string;
}) {
  return (
    <div className={span}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? "mt-1 break-all font-mono text-xs" : "mt-1"}>{value}</div>
    </div>
  );
}
